import { useEffect, useMemo, useRef, useState } from "react";

import { Form, Formik } from "formik";
import TabbedForm from "../../UI/TabbedForm";
import SelectCourrier from "./SelectCourrier";
import CourrierFields from "./CourrierFields";
import { sanitizeValues } from "../../../models/utils";
import {
  handleAPIError,
  showFormikFieldsInError,
} from "../../../utils/index.jsx";
import ChooseSendFileMethod from "../../UI/ChooseSendFileMethod";
import {
  AVIS_ECHEANCE_INITIAL_VALUE,
  avisEcheanceSchema,
  CONGE_LEGITIME_INITIAL_VALUE,
  CONGE_REPRISE_INITIAL_VALUE,
  CONGE_VENTE_INITIAL_VALUE,
  congeLegitimeSchema,
  congeRepriseSchema,
  congeVenteSchema,
  CONVOCATION_EDL_INITIAL_VALUE,
  convocationEdlSchema,
  COURRIER_CHANGEMENT_LOCATAIRE_INITIAL_VALUE,
  COURRIER_IRL_INITIAL_VALUE,
  COURRIER_MODIF_CHARGE_INITIAL_VALUE,
  COURRIER_MODIF_LOYER_INITIAL_VALUE,
  COURRIER_PROROGATION_MOBILITE_INITIAL_VALUE,
  COURRIER_VIDE_INITIAL_VALUE,
  courrierBaseSchema,
  courrierChangementLocataireSchema,
  courrierIrlSchema,
  courrierModifChargeSchema,
  courrierModifLoyerSchema,
  courrierProrogationMobiliteSchema,
  isCourrierWithoutFields,
  REGUL_INITIAL_VALUE,
  regulSchema,
  RESTITUTION_DG_INITIAL_VALUE,
  restitutionDGSchema,
  getTfToemInitialValue,
  tfToemDGSchema,
} from "../../../models/courrier";
import { useNavigate } from "react-router-dom";
import { useTenantProfiles } from "../../../hooks/use-profiles-indiv";
import { Profile, ProfileStatus } from "../../../models/profile";
import ProfileForm from "../../Profile/ProfileForm";
import ButtonCard from "../../UI/ButtonCard";
import { AVOCAT_LINK } from "../../../utils/constants/courrier";
import Spinner from "../../UI/Spinner.jsx";
import useFiles from "../../../hooks/use-files";
import useAuthenticatedAxios from "../../../hooks/use-authenticated-axios";
import ModalDialog, { ModalDialogBody } from "../../UI/ModalDialog";
import styles from "./AvenantForm.module.css";

export default function AvenantForm({
  open,
  onOpenChange,
  tenant,
  onSubmit,
  type,
  balances,
  shortcut,
  resetShortcut,
  property,
}) {
  const [courrierId, setCourrierId] = useState("");
  const [error, setError] = useState("");
  const [editorHTML, setEditorHTML] = useState("");
  const [previewPdfUrl, setPreviewPdfUrl] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const axios = useAuthenticatedAxios();
  const nav = useNavigate();
  const formRef = useRef();

  const tenantId = tenant?.id;

  const { files } = useFiles({ tenantId });
  const bailFile = files.find((file) => file.tag === "bail");

  function replacePreviewPdf(url) {
    setPreviewPdfUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return url;
    });
  }

  useEffect(() => {
    (async function () {
      if (tenantId && isCourrierWithoutFields(courrierId)) {
        setEditorHTML("");
        setIsGenerating(true);
        setError("");
        try {
          const documentHTML = await onSubmit(
            tenantId,
            { courrier_id: courrierId, courrier_content: "" },
            true,
          );
          setEditorHTML(documentHTML);
        } catch (e) {
          setError(handleAPIError(e));
        }
        setIsGenerating(false);
      }
    })();
  }, [courrierId, tenantId, onSubmit]);
  useEffect(() => {
    if (shortcut) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCourrierId(shortcut);
      formRef.current.selectTab("form");
    }
  }, [shortcut]);

  const [profiles, setProfiles] = useState([]);
  const [isAddProfileOpen, setIsAddProfileOpen] = useState(false);
  // La fiche locataire lit ses profils sur une ressource distincte du tenant : un changement
  // de colocataire doit la revalider pour afficher l'entrant et retirer le sortant.
  const { refresh: refreshProfiles } = useTenantProfiles(tenant?.id);
  useEffect(() => {
    if (tenant) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProfiles(
        tenant.profiles.filter(
          (profile) =>
            profile.garantor_of === null &&
            profile.status === ProfileStatus.Actif.status,
        ),
      );
    }
  }, [tenant]);

  // Sur un changement de colocataire, toutes les parties signent l'avenant : ceux qui restent
  // et les entrants (dans `profiles`) mais aussi les sortants, retirés de `profiles` par le
  // sélecteur et encore actifs sur le bail.
  const signataires = useMemo(() => {
    if (courrierId !== "changement_locataire" || !tenant) return profiles;
    const selectedIds = new Set(profiles.map((p) => p.id));
    const sortants = tenant.profiles.filter(
      (p) =>
        p.garantor_of === null &&
        p.status === ProfileStatus.Actif.status &&
        !selectedIds.has(p.id),
    );
    return [...profiles, ...sortants];
  }, [courrierId, tenant, profiles]);

  const [isSwitchingCourrierId, setIsSwitchingCourrierId] = useState(false);
  useEffect(() => {
    if (isSwitchingCourrierId) {
      // Hack to prevent rendering forms while formik is changing initial values and schemas
      // This is to prevent inner CourrierFields from rendering things depending on formik
      // values before they are initialized properly after a courrier change
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsSwitchingCourrierId(false);
    }
  }, [isSwitchingCourrierId]);

  async function submitHandler(
    values,
    { resetForm, setSubmitting },
    preview = false,
  ) {
    setSubmitting(true);
    const sanitizedValues = { ...sanitizeValues(values) };
    sanitizedValues.courrier_id = courrierId;
    sanitizedValues.courrier_content = editorHTML;
    if (courrierId === "changement_locataire" && profiles) {
      sanitizedValues.profiles = profiles;
    }
    setError("");
    try {
      if (preview === true) {
        const response = await onSubmit(tenant.id, sanitizedValues, true);
        if (courrierId === "avis_echeance") {
          // Blob same-origin plutôt qu'URL signée : l'iframe cross-origin est
          // bloquée par X-Frame-Options
          const blobResponse = await axios.get(response.download_url, {
            responseType: "blob",
          });
          replacePreviewPdf(URL.createObjectURL(blobResponse.data));
        } else {
          setEditorHTML(response);
        }
      } else {
        await onSubmit(tenant.id, sanitizedValues);
        if (courrierId === "changement_locataire") {
          await refreshProfiles();
        }
        resetForm();
        setCourrierId("");
        setEditorHTML("");
        replacePreviewPdf("");
        resetShortcut();
        formRef.current?.resetTab();
        onOpenChange(false);
        nav(
          `/bailleur/tenants/${tenant.id}?tab-tenant-show=documents-courriers`,
        );
      }
    } catch (error) {
      setError(handleAPIError(error));
    }
    setSubmitting(false);
  }
  let initialValues, validationSchema;
  switch (courrierId) {
    case "modif_charge":
      initialValues = COURRIER_MODIF_CHARGE_INITIAL_VALUE;
      validationSchema = courrierModifChargeSchema;
      break;
    case "modif_loyer":
      initialValues = COURRIER_MODIF_LOYER_INITIAL_VALUE;
      validationSchema = courrierModifLoyerSchema;
      break;
    case "prorogation_mobilite":
      initialValues = COURRIER_PROROGATION_MOBILITE_INITIAL_VALUE;
      validationSchema = courrierProrogationMobiliteSchema;
      break;
    case "changement_locataire":
      initialValues = COURRIER_CHANGEMENT_LOCATAIRE_INITIAL_VALUE;
      validationSchema = courrierChangementLocataireSchema;
      break;
    case "conge_legitime":
    case "courrier_med_trouble":
    case "courrier_trouble":
      initialValues = CONGE_LEGITIME_INITIAL_VALUE;
      validationSchema = congeLegitimeSchema;
      break;
    case "conge_reprise_nu":
    case "conge_reprise_meuble":
      initialValues = CONGE_REPRISE_INITIAL_VALUE;
      validationSchema = congeRepriseSchema;
      break;
    case "conge_vente_nu":
      initialValues = CONGE_VENTE_INITIAL_VALUE;
      validationSchema = congeVenteSchema;
      break;
    case "regul_charge":
      initialValues = REGUL_INITIAL_VALUE;
      validationSchema = regulSchema;
      break;
    case "courrier_irl":
      initialValues = COURRIER_IRL_INITIAL_VALUE;
      validationSchema = courrierIrlSchema;
      break;
    case "convocation_edl":
      initialValues = CONVOCATION_EDL_INITIAL_VALUE;
      validationSchema = convocationEdlSchema;
      break;
    case "restitution_dg":
      initialValues = RESTITUTION_DG_INITIAL_VALUE;
      validationSchema = restitutionDGSchema;
      break;
    case "courrier_tf":
    case "courrier_toem":
      initialValues = getTfToemInitialValue(tenant);
      validationSchema = tfToemDGSchema;
      break;
    case "avis_echeance":
      initialValues = AVIS_ECHEANCE_INITIAL_VALUE;
      validationSchema = avisEcheanceSchema;
      break;
    case "courrier_template":
    case "courrier_changement_proprietaire":
    case "courrier_justificatif_assurance":
    case "courrier_med_assurance":
    case "conge_vente_meuble":
    case "courrier_justificatif_entretien":
    case "courrier_med_justificatif_entretien":
    case "courrier_impaye":
    case "courrier_caution_impaye":
    case "courrier_med_impaye":
    case "courrier_med_caution_impaye":
    case "courrier_trouble_caution":
    case "avenant_libre":
    case "rappel_fin_bail":
    case "recu_dg":
      initialValues = COURRIER_VIDE_INITIAL_VALUE;
      validationSchema = courrierBaseSchema;
      break;
    default:
      initialValues = {};
      validationSchema = null;
  }

  return (
    <>
      <ProfileForm
        onSubmit={(p) => {
          setProfiles((cur) => [...cur, Profile.create(p)]);
          setIsAddProfileOpen(false);
        }}
        open={isAddProfileOpen}
        onOpenChange={setIsAddProfileOpen}
        onCancel={() => setIsAddProfileOpen(false)}
      />
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={submitHandler}
        validationSchema={validationSchema}
      >
        {(formikProps) => {
          let tabIndex = 0;
          function handleOpenChange(nextOpen) {
            if (!nextOpen) {
              setEditorHTML("");
              replacePreviewPdf("");
              setError("");
              setCourrierId("");
              resetShortcut();
              formRef.current?.resetTab();
              formikProps.resetForm();
            }
            onOpenChange(nextOpen);
          }
          return (
            <>
              <TabbedForm id="avenant-form" ref={formRef}>
                <ModalDialog
                  open={open}
                  onOpenChange={handleOpenChange}
                  size="xl"
                  tabsHeader={
                    <TabbedForm.TabList>
                      <TabbedForm.Tab panelId="type-avenant" index={tabIndex++}>
                        {type === "avenant" && "Type d'avenant"}
                        {type === "courrier" && "Type de courrier"}
                        {type === "conge" && "Type de congé"}
                      </TabbedForm.Tab>

                      {Object.keys(initialValues).length > 0 && (
                        <>
                          <TabbedForm.Tab panelId="form" index={tabIndex++}>
                            Champs à modifier
                          </TabbedForm.Tab>
                          <TabbedForm.Tab panelId="document" index={tabIndex++}>
                            Validation & envoi
                          </TabbedForm.Tab>
                        </>
                      )}
                    </TabbedForm.TabList>
                  }
                >
                  <Form>
                    <TabbedForm.TabPanel id="type-avenant">
                      <ModalDialogBody className={styles.typePanel}>
                        {type === "courrier" && (
                          <ButtonCard
                            className={
                              "primaryText text-center w-100 marginB10"
                            }
                            variant="secondary"
                            onClick={() => window.open(AVOCAT_LINK, "_blank")}
                          >
                            <p>
                              Envoyez une mise en demeure, validée et signée par
                              nos avocats partenaires
                            </p>
                            <img src={"/justice_lib.png"} alt="avocats" />
                          </ButtonCard>
                        )}
                        <SelectCourrier
                          onSelectCourrier={(courrierId) => {
                            setCourrierId(courrierId);
                            setIsSwitchingCourrierId(true);
                          }}
                          tenant={tenant}
                          type={type}
                        />
                      </ModalDialogBody>
                    </TabbedForm.TabPanel>
                    <TabbedForm.TabPanel id="form">
                      {isSwitchingCourrierId && (
                        <ModalDialogBody>
                          <Spinner width={"3em"} />
                        </ModalDialogBody>
                      )}
                      {!isSwitchingCourrierId && (
                        <CourrierFields
                          error={error}
                          balances={balances}
                          property={property}
                          courrierId={courrierId}
                          tenant={tenant}
                          content={editorHTML}
                          previewPdfUrl={previewPdfUrl}
                          isSubmitting={
                            formikProps.isSubmitting || isGenerating
                          }
                          onContentChange={setEditorHTML}
                          profiles={profiles}
                          onSelectProfile={setProfiles}
                          onCreateProfile={() => setIsAddProfileOpen(true)}
                          isAvenant={type === "avenant"}
                          bailFile={bailFile}
                          onPreview={async () => {
                            const errors = await formikProps.validateForm();
                            if (Object.keys(errors).length === 0) {
                              setEditorHTML("");
                              replacePreviewPdf("");
                              return submitHandler(
                                formikProps.values,
                                formikProps,
                                true,
                              );
                            }
                            showFormikFieldsInError(
                              errors,
                              formikProps.setFieldTouched,
                            );
                          }}
                        />
                      )}
                    </TabbedForm.TabPanel>
                    <TabbedForm.TabPanel id="document">
                      <ChooseSendFileMethod
                        propertyId={tenant?.id_bien}
                        profiles={signataires}
                        tenant={tenant}
                        typeSend={type === "avenant" ? "elec" : "lrar"}
                        error={error}
                        sticky
                      />
                    </TabbedForm.TabPanel>
                  </Form>
                </ModalDialog>
              </TabbedForm>
            </>
          );
        }}
      </Formik>
    </>
  );
}
